import { ChangeEvent, type JSX, KeyboardEvent, useRef, useState } from "react";
import clsx from "clsx";
import { inject, observer } from "mobx-react";
import { Store } from "stores/store";
import { motion } from "framer-motion";

type Props = {
    onSubmit: (message: string) => Promise<void>;
    className?: string;
    disabled?: boolean;
    visible?: boolean;
    animationDelay: number;
    store?: Store;
};

const ChatTextInput = ({
    onSubmit,
    className = "",
    disabled = false,
    visible = true,
    store,
    animationDelay,
}: Props): JSX.Element => {
    const textInputRef = useRef<HTMLTextAreaElement>(null);

    const [isInputDisabled, setIsInputDisabled] = useState(disabled);

    if (!store) {
        throw Error("Store was not setup correctly");
    }

    const { updateChatBackground, updateChatEffect, chatbotLocalized } = store;
    const inputDefaultValue = chatbotLocalized?.inputDefaultValue ?? "";

    const [textInputValue, setTextInputValue] = useState(inputDefaultValue);

    const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setTextInputValue(event.target.value);
    };

    const handleSubmit = () => {
        if (textInputValue.trim() !== "" && textInputValue !== inputDefaultValue) {
            onSubmit(textInputValue);
            setTextInputValue("");
            if (textInputRef.current) {
                textInputRef.current.blur();
                textInputRef.current.value = "";
            }
            updateChatBackground("");
            updateChatEffect("");
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter") {
            handleSubmit();
        }
    };

    const handleFocus = () => {
        if (textInputValue === inputDefaultValue) {
            setTextInputValue("");
        }
    };

    const handleBlur = () => {
        if (textInputValue === "") {
            setTextInputValue(inputDefaultValue);
        }
    };

    const handleAnimationComplete = () => {
        setIsInputDisabled(disabled);
    };

    return (
        <motion.div
            className={clsx(
                "bg-[rgba(0,0,0,0.2)] backdrop-blur-2xl border-[#FFF] border-[1.5px] text-white ChatTextInput |",
                "shadow-[inset_0_-0.74px_0.74px_0_rgba(255,255,255,0.35)]",
                "mb-[-1px] w-full",
                "rounded-[10px] 2xs:rounded-[14px]",
                !visible ? "hidden" : "visible",
                className,
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: disabled ? "0.3" : "1" }}
            transition={{ delay: disabled ? 0 : animationDelay, duration: disabled ? 0 : 0.3, ease: "linear" }}
            onAnimationComplete={handleAnimationComplete}
        >
            <div
                className={clsx(
                    "flex items-end gap-4 w-full min-w-0",
                    "w-full px-4",
                    "py-[7px] 2xs:py-[14.5px]",
                    "rounded-[10px] 2xs:rounded-[14px]",
                    "shadow-[inset_1.49px_2.97px_2.97px_-0.74px_rgba(0,0,0,0.25)]",
                )}
            >
                <div className="relative flex-1 min-w-0">
                    <textarea
                        ref={textInputRef}
                        rows={1}
                        className="top-0 absolute bg-transparent px-0 py-0 border-none focus:outline-0 focus:ring-0 w-full h-full max-h-[64px] font-frutigerRoman text-[16px] resize-none"
                        value={textInputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        disabled={isInputDisabled}
                    />
                    {/* Using an invisble div to grow the textarea when multiple lines are entered */}
                    <div className="invisible w-full min-h-6 max-h-[64px] overflow-hidden font-frutigerRoman text-[16px] break-words whitespace-pre-wrap">
                        {textInputValue}
                    </div>
                </div>
                <button type="button" onClick={handleSubmit} className="shrink-0 self-end" disabled={isInputDisabled}>
                    <svg
                        width="22"
                        height="23"
                        viewBox="0 0 22 23"
                        fill="none"
                        className="w-[20px] 2xs:w-[24px] h-[20px] 2xs:h-[24px]"
                    >
                        <g opacity="0.7">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M20.5737 1.54947C20.6694 1.64505 20.7352 1.76638 20.7632 1.8987C20.7912 2.03101 20.7801 2.16861 20.7314 2.29476L13.753 20.4385C13.7036 20.5669 13.6174 20.6778 13.5051 20.7574C13.3929 20.8369 13.2596 20.8815 13.1221 20.8855C12.9846 20.8895 12.849 20.8528 12.7323 20.78C12.6156 20.7072 12.523 20.6015 12.4662 20.4762L9.54784 14.0561L13.8409 9.76162C14.0258 9.56319 14.1265 9.30073 14.1217 9.02955C14.1169 8.75837 14.0071 8.49964 13.8153 8.30785C13.6235 8.11607 13.3648 8.00621 13.0936 8.00143C12.8224 7.99664 12.56 8.0973 12.3615 8.2822L8.06703 12.5753L1.64693 9.65834C1.52128 9.60159 1.41525 9.50888 1.34223 9.39193C1.26921 9.27497 1.23247 9.13901 1.23665 9.00119C1.24083 8.86338 1.28574 8.72989 1.36572 8.61758C1.44569 8.50526 1.55715 8.41916 1.68601 8.37013L19.8298 1.39176C19.9557 1.34329 20.0931 1.3324 20.2251 1.36039C20.3571 1.38838 20.4782 1.45406 20.5737 1.54947Z"
                                fill="white"
                            />
                        </g>
                    </svg>
                </button>
            </div>
        </motion.div>
    );
};

export default inject("store")(observer(ChatTextInput));
